import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Navigation, Loader2, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ListingCard } from '@/components/ListingCard';
import { HorizontalScrollSection, ScrollCard } from '@/components/HorizontalScrollSection';
import { geocodeAddress, reverseGeocode } from '@/hooks/useGooglePlacesAutocomplete';

interface Event {
  id: string;
  slug: string;
  title: string;
  date: string;
  time: string;
  background_image_url: string;
  target_date: string;
  address: string;
  city: string | null;
  country: string | null;
  category: string | null;
  is_free: boolean;
}

interface EventWithDistance extends Event {
  distance: number;
}

// Cache for geocoded addresses
const geocodeCache: Record<string, [number, number]> = {};

// Calculate distance between two points using Haversine formula (in km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Convert km to miles
function kmToMiles(km: number): number {
  return km * 0.621371;
}

interface NearbyEventsProps {
  isFavorite?: (id: string) => boolean;
  onFavoriteToggle?: (id: string) => void;
}

export const NearbyEvents = ({ isFavorite, onFavoriteToggle }: NearbyEventsProps) => {
  const navigate = useNavigate();
  const [nearbyEvents, setNearbyEvents] = useState<EventWithDistance[]>([]);
  const [loading, setLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied' | 'error'>('idle');
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [cityName, setCityName] = useState<string | null>(null);

  // Check permission status and cached location on mount
  useEffect(() => {
    // Try to get cached location first
    const cachedLocation = localStorage.getItem('userLocation');
    if (cachedLocation) {
      try {
        const parsed = JSON.parse(cachedLocation);
        setUserLocation(parsed);
        setLocationStatus('granted');
      } catch (e) {
        localStorage.removeItem('userLocation');
      }
    }

    // Check if we have permission already granted
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          // Permission already granted, get fresh location
          requestLocation();
        } else if (result.state === 'denied') {
          setLocationStatus('denied');
          localStorage.removeItem('userLocation');
        }
        // If 'prompt', stay idle unless we have cached location
      }).catch(() => {
        // Permissions API not fully supported, rely on cached location
      });
    }
  }, []);

  // Request user's location
  const requestLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('error');
      return;
    }

    setLocationStatus('requesting');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        };
        setUserLocation(location);
        setLocationStatus('granted');
        // Cache the location
        localStorage.setItem('userLocation', JSON.stringify(location));
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationStatus(error.code === 1 ? 'denied' : 'error');
        localStorage.removeItem('userLocation');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Geocode address using Google
  const geocodeAddressWithCache = async (address: string): Promise<[number, number] | null> => {
    if (geocodeCache[address]) {
      return geocodeCache[address];
    }

    const coords = await geocodeAddress(address);
    if (coords) {
      // coords is [lng, lat], we need [lat, lon] for distance calculation
      geocodeCache[address] = [coords[1], coords[0]];
      return [coords[1], coords[0]];
    }
    return null;
  };

  // Reverse geocode to get city name
  useEffect(() => {
    if (!userLocation) return;

    const getCityName = async () => {
      try {
        const city = await reverseGeocode(userLocation.lat, userLocation.lon);
        if (city) {
          setCityName(city);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    };

    getCityName();
  }, [userLocation]);

  // Fetch and sort events by distance when location is available
  useEffect(() => {
    if (!userLocation) return;

    const fetchNearbyEvents = async () => {
      setLoading(true);
      
      try {
        // Fetch upcoming events
        const { data: events, error } = await supabase
          .from('events')
          .select('id, slug, title, date, time, background_image_url, target_date, address, city, country, category, is_free')
          .eq('is_hidden', false)
          .gte('target_date', new Date().toISOString())
          .order('target_date', { ascending: true })
          .limit(20);

        if (error) throw error;
        if (!events || events.length === 0) {
          setNearbyEvents([]);
          setLoading(false);
          return;
        }

        // Geocode events and calculate distances
        const eventsWithDistance: EventWithDistance[] = [];
        
        for (const event of events) {
          const coords = await geocodeAddressWithCache(event.address);
          if (coords) {
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lon,
              coords[0],
              coords[1]
            );
            eventsWithDistance.push({ ...event, distance });
          }
        }

        // Sort by distance and take top 8
        eventsWithDistance.sort((a, b) => a.distance - b.distance);
        setNearbyEvents(eventsWithDistance.slice(0, 8));
      } catch (error) {
        console.error('Error fetching nearby events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNearbyEvents();
  }, [userLocation]);

  // Don't render if location was denied
  if (locationStatus === 'denied') {
    return null;
  }

  // Initial state - show prompt to enable location
  if (locationStatus === 'idle') {
    return (
      <div className="mb-12 p-6 bg-gradient-to-r from-brand/10 to-accent/10 rounded-2xl border border-border">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-brand/20 rounded-full flex items-center justify-center">
              <Navigation className="w-6 h-6 text-brand" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Discover Events Near You</h2>
              <p className="text-muted-foreground text-sm">
                Enable location to see events closest to you
              </p>
            </div>
          </div>
          <Button onClick={requestLocation} className="gap-2 rounded-full">
            <MapPin className="w-4 h-4" />
            Enable Location
          </Button>
        </div>
      </div>
    );
  }

  // Loading states
  if (locationStatus === 'requesting' || loading) {
    return (
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Navigation className="w-5 h-5 text-brand" />
            <h2 className="text-xl font-semibold">Events Near You</h2>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">
            {locationStatus === 'requesting' ? 'Getting your location...' : 'Finding nearby events...'}
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (locationStatus === 'error') {
    return (
      <div className="mb-12 p-6 bg-muted/50 rounded-2xl border border-border">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
            <MapPin className="w-6 h-6 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Location Unavailable</h2>
            <p className="text-muted-foreground text-sm">
              We couldn't access your location. Check your browser settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // No nearby events
  if (nearbyEvents.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <HorizontalScrollSection
        title={`Events Near ${cityName ? cityName : 'You'}`}
        titleIcon={<Navigation className="w-5 h-5 text-brand" />}
        action={
          <Button 
            variant="ghost" 
            size="sm" 
            className="gap-1 text-muted-foreground hover:text-foreground"
            onClick={() => navigate('/map')}
          >
            View on Map
            <ChevronRight className="w-4 h-4" />
          </Button>
        }
      >
        {nearbyEvents.map((event, index) => (
          <ScrollCard key={event.id}>
            <div 
              className="relative animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'both' }}
            >
              <ListingCard
                id={event.id}
                slug={event.slug}
                title={event.title}
                subtitle={event.city || event.address}
                image={event.background_image_url}
                date={event.date}
                time={event.time}
                category={event.category}
                linkPrefix="/event"
                showFavorite={!!onFavoriteToggle}
                isFavorite={isFavorite?.(event.id) || false}
                onFavoriteToggle={onFavoriteToggle}
                isFree={event.is_free}
              />
              {/* Distance badge - positioned below other badges */}
              <div className="absolute top-12 left-3 z-10">
                <span className="px-2 py-1 bg-background/90 backdrop-blur-sm text-xs font-medium rounded-full border border-border">
                  {kmToMiles(event.distance) < 1 
                    ? `${(kmToMiles(event.distance) * 5280).toFixed(0)} ft away`
                    : `${kmToMiles(event.distance).toFixed(1)} mi away`
                  }
                </span>
              </div>
            </div>
          </ScrollCard>
        ))}
      </HorizontalScrollSection>
    </div>
  );
};
